import {
	faAngleRight as fasAngleRight,
	faCircleInfo as fasCircleInfo,
	faCircleQuestion as fasCircleQuestion,
	faLightbulbExclamationOn as fasLightbulbExclamationOn,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import type {
	AbstractResultItemProps,
	AbstractResultItemState,
} from "@component/assets/AbstractListingCalculator/AbstractResultItem";
import { AbstractResultItem } from "@component/assets/AbstractListingCalculator/AbstractResultItem";
import { DataBox } from "@component/assets/AbstractListingCalculator/components/DataBox";
import { MarketingAccordion } from "@component/assets/AbstractListingCalculator/components/MarketingAccordion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ExpertBlockquote } from "@ui/components/ExpertBlockquote";
import { formatText } from "@ui/format";
import { Button, HtmlBlock, InfoPopup, List, addParameterToURL } from "bm-ui-kit";
import compact from "lodash/compact";
import lzString from "lz-string";
import type { ReactNode } from "react";
import { Col, Modal, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import type { OverlayInjectedProps } from "react-bootstrap/esm/Overlay";
import { PersonalLoanLeadForm } from "../LeadForm/PersonalLoanLeadForm";
import type { PersonalLoanLeadFormProps } from "../LeadForm/PersonalLoanLeadForm";
import { LOAN_GOALS } from "./data";
import { getInterestName, getLeadCalculatorRequest, getLeadCalculatorResponse } from "./domain";
import type { PersonalLoanRequest, PersonalLoanResponseItem, PersonalLoanSearchParams } from "./types";

const EXPENSE_TYPES: { code: string; name: string }[] = [
	{ code: "ONE_TIME", name: "Induló díjak" },
	{ code: "MONTHLY", name: "Rendszeres díjak" },
	{ code: "OPTIONAL", name: "Egyéb díjak" },
];

interface Props
	extends AbstractResultItemProps<PersonalLoanSearchParams, PersonalLoanRequest, PersonalLoanResponseItem> {
	mobileView?: boolean;
	hideLeadForm?: boolean;
}

interface State extends AbstractResultItemState {
	showDetailsModal: boolean;
	detailsModalContent: ReactNode;
	detailsModalTitle: ReactNode;
}

export class PersonalLoanResultItem extends AbstractResultItem<
	PersonalLoanSearchParams,
	PersonalLoanRequest,
	PersonalLoanResponseItem,
	Props,
	State
> {
	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			showDetailsModal: false,
			detailsModalContent: "",
			detailsModalTitle: "",
		};
	}

	protected renderHeader(name?: string): ReactNode {
		return super.renderHeader(name, "product-link text-truncate");
	}

	protected renderHeaderTitle(name?: string): ReactNode {
		const { result, mobileView = false } = this.props;
		const resultName = name || result.name;

		if (mobileView) {
			return (
				<>
					{result?.bank?.name && <span className="result-bank dash-after">{result.bank.name}</span>}
					<strong className="result-name">{resultName}</strong>
					{result?.subType && <span className="result-subtype">{` ${result.subType} `}</span>}
				</>
			);
		}

		return (
			<>
				<strong className="result-name">{resultName}</strong>
				{result?.subType && <span className="result-subtype">{` ${result.subType} `}</span>}
				{result?.bank?.name && <span className="result-bank dash-before">{result.bank.name}</span>}
			</>
		);
	}

	protected renderProductData(): ReactNode {
		const {
			result,
			request: { comparator },
			mobileView = false,
		} = this.props;

		const dataBoxClass = "col-4 col-md-3";

		return (
			<>
				{this.rendersStartInstallmentDataBox(
					result,
					compact([dataBoxClass, comparator === "START_INSTALLMENT" && "ordered-by"]).join(" "),
				)}
				<DataBox
					className={dataBoxClass}
					orderBy={comparator === "APR"}
					title="THM"
					value={formatText(result.apr, "%")}
					highlightOrderedValue
				/>
				<DataBox
					className={dataBoxClass}
					orderBy={comparator === "TOTAL_REDEMPTION"}
					title={mobileView ? "visszafizetés" : "teljes visszafizetés"}
					value={formatText(result.totalRedemption, "ft")}
					highlightOrderedValue
				/>
				<DataBox
					className={`${dataBoxClass} d-none d-md-block`}
					title="fix törlesztő"
					value={getInterestName(result.interestPeriod, true)}
				/>
				{this.renderDetailsModal()}
			</>
		);
	}

	protected renderFeedback(): ReactNode {
		const { feedBackStatus } = this.state;
		const { result, hideLeadForm } = this.props;

		if (!feedBackStatus) {
			return;
		}

		return (
			<Row id={`feedback-${result.id}`} className="feedback mx-0">
				{feedBackStatus === "details" && (
					<Col lg={7} className="details block py-3 order-lg-1">
						{this.renderFeedbackDetails()}
					</Col>
				)}
				<Col lg={5} className={`${hideLeadForm ? "bg-white" : "lead-block bg-success"} p-0 order-lg-2`}>
					{this.renderFeedbackLead(this.getLeadProps())}
				</Col>
				{feedBackStatus === "interest" && (
					<Col lg={7} className="interest block py-3 order-lg-1">
						{this.renderFeedbackInterest()}
					</Col>
				)}
			</Row>
		);
	}

	private rendersStartInstallmentDataBox(result: PersonalLoanResponseItem, className: string): ReactNode {
		const {
			request: { comparator },
			mobileView = false,
		} = this.props;
		const orderBy = comparator === "START_INSTALLMENT";
		const installMentName = mobileView ? "törlesztő" : "törlesztőrészlet";

		if (result.gracePeriodInstallment) {
			return this.renderDoubleDataBox(
				<OverlayTrigger
					overlay={(props: OverlayInjectedProps) => (
						<Tooltip id={`infoTooltip-${result.name}-graceperiod`} className="tooltip-primary" {...props}>
							A türelmi idő alatti törlesztőrészletek összege minden hónapban eltér, mivel a napok száma
							alapján kerül kiszámításra.
						</Tooltip>
					)}
				>
					<span>
						türelmi idős törlesztőrészlet <FontAwesomeIcon icon={fasCircleInfo} />
					</span>
				</OverlayTrigger>,
				formatText(result.gracePeriodInstallment, "ft"),
				<OverlayTrigger
					overlay={(props: OverlayInjectedProps) => (
						<Tooltip id={`infoTooltip-${result.name}-graceperiod`} className="tooltip-primary" {...props}>
							A 13. hónaptól érvényes törlesztőrészlet, amely már tőkét is tartalmaz.
						</Tooltip>
					)}
				>
					<span>
						havi törlesztőrészlet <FontAwesomeIcon icon={fasCircleInfo} />
					</span>
				</OverlayTrigger>,
				formatText(result.gracePeriodInstallment, "ft"),
				className,
				orderBy,
			);
		}

		if (result.weeklyStartInstallment) {
			return (
				<DataBox
					className={className}
					orderBy={orderBy}
					title={`heti ${installMentName}`}
					value={formatText(result.weeklyStartInstallment, "ft")}
					highlightOrderedValue
				/>
			);
		}

		return (
			<DataBox
				className={className}
				orderBy={orderBy}
				title={
					<>
						{`havi ${installMentName}`}

						{!result.marketing?.newTargetUrl && (
							<Button
								className="d-none d-lg-flex p-1 text-underlined"
								variant="link"
								icon={
									<FontAwesomeIcon
										icon={fasLightbulbExclamationOn}
										className="text-success"
										size="lg"
									/>
								}
								onClick={() => this.onInterestClick("reduce-payment")}
							>
								Csökkentsd a törlesztőt
							</Button>
						)}
					</>
				}
				value={formatText(result.startInstallment, "ft")}
			/>
		);
	}

	private renderDoubleDataBox(
		text1: ReactNode,
		value1: ReactNode,
		text2: ReactNode,
		value2: ReactNode,
		colClasses: string,
		highlight = false,
	): ReactNode {
		const valueClassName = highlight ? "font-weight-black" : undefined;

		return (
			<div
				className={`data-box text-center flex-sm-column justify-content-sm-center justify-content-between align-items-center flex-row-reverse d-flex py-lg-3 py-sm-2 py-1 px-sm-3 px-2 ${colClasses}`}
			>
				<span className="data-description d-block w-100 text-center">
					<strong className={valueClassName}>{value1}</strong>
					<br />
					{text1}
					<br />
					<strong className={valueClassName}>{value2}</strong>
					<br />
					{text2}
				</span>
			</div>
		);
	}

	protected renderFeedbackLead(props: PersonalLoanLeadFormProps): ReactNode {
		const { searchParams, result, hideLeadForm } = this.props;
		const dataPageUrl = this.getFullDataPageUrl();

		return (
			<>
				{!hideLeadForm && (
					<PersonalLoanLeadForm
						withOnlineOption={false}
						withRequisitionTimeOption={false}
						useListingCalculatorVersion
						request={getLeadCalculatorRequest(searchParams)}
						response={getLeadCalculatorResponse(result)}
						{...props}
					/>
				)}

				<div className="p-3 bg-white d-lg-none">
					<Button
						variant="light"
						rel="nofollow"
						href={dataPageUrl}
						target="_blank"
						onClick={this.onProductPageClick}
					>
						További részletek <FontAwesomeIcon icon={fasAngleRight} className="ml-2" />
					</Button>
				</div>
			</>
		);
	}

	protected renderFeedbackInterest(): ReactNode {
		const { result } = this.props;

		return (
			<>
				<div className="display-4 display-bold text-primary">
					Szakértőink díjmentesen segítenek csökkenteni a {formatText(result.startInstallment, "ft")}{" "}
					törlesztőt!
				</div>
				<List itemStyle="tick" variant="success" className="ml-2 my-4">
					<List.Item>
						Megkeressük azt a <strong>futamidőt</strong>, amely mellett a{" "}
						<strong>törlesztő a legkedvezőbb</strong> és a teljes visszafizetés sem ugrik meg jelentősen.
					</List.Item>
					<List.Item>
						Folyamatosan nyomon követjük helyetted a piacot és szólunk, ha{" "}
						<strong>meglévő kölcsönöd kedvezőbbre cserélheted!</strong>
					</List.Item>
				</List>
				<ExpertBlockquote
					expertId="dekany"
					quote="Segítek! Kérd ingyenes személyre szabott ajánlatodat!"
					title="személyi kölcsön szakértő"
				/>
			</>
		);
	}

	protected renderFeedbackDetails(): ReactNode {
		const { result } = this.props;

		const dataPageUrl = this.getFullDataPageUrl();

		return (
			<>
				<div className="h4 miert-jobb">Hasznos tudnivalók a hitelről:</div>
				<List itemStyle="tick" variant="primary" className="ml-2">
					<List.Item>
						<InfoPopup className="d-inline-block" tooltip="A hitel célja.">
							<strong>Hitelcél:</strong> {LOAN_GOALS.find((lg) => result.loanGoal === lg.value).label}
							<FontAwesomeIcon icon={fasCircleQuestion} className="ml-1" />
						</InfoPopup>
					</List.Item>
					<List.Item>
						<InfoPopup className="d-inline-block" tooltip="A hitel éves kamata.">
							<strong>Kamat:</strong> {formatText(result.interestRate, "%")}
							<FontAwesomeIcon icon={fasCircleQuestion} className="ml-1" />
						</InfoPopup>
					</List.Item>
					<List.Item>
						<InfoPopup
							className="d-inline-block"
							tooltip="Ilyen időközönként emelkedhet vagy csökkenhet a hitel kamata és törlesztőrészlete a futamidő alatt."
						>
							<strong>Kamatperiódus:</strong> {getInterestName(result.interestPeriod)}
							<FontAwesomeIcon icon={fasCircleQuestion} className="ml-1" />
						</InfoPopup>
					</List.Item>
				</List>

				{this.renderExpenses()}

				<p className="d-none d-md-block mb-0">
					Teljeskörű támogatás a hiteligénylésedhez.
					<br />
					<strong>A Bankmonitor segít, amíg a pénzt megkapod!</strong>
				</p>

				<Button
					variant="light"
					className="mt-4 d-none d-lg-inline-block"
					rel="nofollow"
					href={dataPageUrl}
					target="_blank"
					onClick={this.onProductPageClick}
				>
					További részletek <FontAwesomeIcon icon={fasAngleRight} className="ml-2" />
				</Button>
			</>
		);
	}

	private renderExpenses(): ReactNode {
		const { result } = this.props;
		const { mobileView = false } = this.props;

		return (
			<>
				{EXPENSE_TYPES.filter(
					({ code }) => (mobileView && !["OPTIONAL", "MONTHLY"].includes(code)) || !mobileView,
				)
					.map(({ code, name }) => {
						const items = result.expenses[code].filter(({ expenseType }) => expenseType !== "KK");

						return { code, name, items };
					})
					.filter(({ items }) => items?.length)
					.map(({ code, name, items }) => (
						<div key={code}>
							<span className="h4 expenses-header">{name}</span>
							<List itemStyle="tick" variant="primary" className="ml-2">
								{items.map((expense) =>
									expense.expenseType === "FJ" ? (
										<List.Item key={expense.expenseType}>
											<InfoPopup
												className="d-inline-block"
												tooltip="Folyósított összegből kerül levonásra!"
											>
												<strong>{expense.name}:</strong> {expense.value || "-"}
												<FontAwesomeIcon icon={fasCircleQuestion} className="ml-1" />
											</InfoPopup>
										</List.Item>
									) : (
										<List.Item key={expense.expenseType}>
											<strong>{expense.name}:</strong> {!!expense.value && expense.value}
											{!expense.value && !!expense.numericValue && expense.numericValue}
											{!!expense.description && (
												<Button
													className="ml-1"
													variant="light"
													size="sm"
													icon={<FontAwesomeIcon icon={fasCircleInfo} />}
													onClick={() =>
														this.onDetailsButtonClick(expense.name, expense.description)
													}
												>
													Részletek
												</Button>
											)}
										</List.Item>
									),
								)}
							</List>
						</div>
					))}
			</>
		);
	}

	protected renderMarketingData(): ReactNode {
		const {
			result: { marketing },
		} = this.props;

		if (!marketing?.texts?.plus) {
			return null;
		}
		const props = { xl: 9, md: 7 };

		if (marketing?.highlight) {
			return (
				<MarketingAccordion colProps={props}>
					<HtmlBlock
						className="marketing-text font-weight-light my-2 inline-ul"
						html={marketing?.texts.plus}
					/>
				</MarketingAccordion>
			);
		}

		return (
			<Col className="marketing-block" {...props}>
				<HtmlBlock className="marketing-text font-weight-light my-2 inline-ul" html={marketing?.texts.plus} />
			</Col>
		);
	}

	protected getDataPageUrl(): string {
		const { result, searchParams, hideLeadForm } = this.props;

		return addParameterToURL(`/szemelyi-kolcson/adatlap/`, {
			ID: result.id,
			d: lzString.compressToEncodedURIComponent(JSON.stringify(searchParams)),
			tab: "PERSONAL_LOAN",
			leadForm: hideLeadForm ? "hide" : "show",
		});
	}

	private renderDetailsModal(): ReactNode {
		const { showDetailsModal, detailsModalContent, detailsModalTitle } = this.state;
		const handleClose: VoidFunction = () => {
			this.setState({
				showDetailsModal: false,
			});
		};

		return (
			<Modal show={showDetailsModal} onHide={handleClose} centered>
				<Modal.Header closeButton>
					<Modal.Title>{detailsModalTitle || "Részletek"}</Modal.Title>
				</Modal.Header>
				<Modal.Body>{detailsModalContent}</Modal.Body>
			</Modal>
		);
	}

	private onDetailsButtonClick(modalTitle: ReactNode, modalContent: ReactNode): void {
		const { showDetailsModal } = this.state;
		this.setState({
			showDetailsModal: !showDetailsModal,
			detailsModalContent: modalContent,
			detailsModalTitle: modalTitle,
		});
	}

	protected getComponentName(): string {
		return "PersonalLoanResultItem";
	}
}
